import { Component, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ViewerBackService } from '../../../viewer/core/services/viewer-back.service';

/**
 * Botón de "volver" global, para toda la app fuera del visor.
 *
 * Antes la única forma de retroceder era la flecha física del dispositivo:
 * en equipos sin esa tecla (iPhone en PWA instalada, por ejemplo) no había
 * manera de salir de una pantalla anidada salvo refrescar la página entera.
 *
 * Reutiliza {@link ViewerBackService}: aunque nació para el visor, cuenta la
 * navegación de TODA la app desde el arranque (se instancia en
 * `app.config.ts`), así que sirve igual acá.
 */
@Component({
  selector: 'app-back-button',
  standalone: true,
  imports: [],
  templateUrl: './back-button.component.html',
  styleUrl: './back-button.component.scss',
})
export class BackButtonComponent {
  private readonly router = inject(Router);
  private readonly back = inject(ViewerBackService);

  /** El visor ya trae su propia flecha "Volver" en cada pantalla. */
  private esVisor(url: string): boolean {
    return url.startsWith('/viewer');
  }

  /** En login/registro/recuperación no hay nada útil a lo que volver. */
  private esAuth(url: string): boolean {
    return (
      url === '/' ||
      url.startsWith('/login') ||
      url.startsWith('/register') ||
      url.startsWith('/forgot')
    );
  }

  private ocultarPorRuta(url: string): boolean {
    return this.esVisor(url) || this.esAuth(url);
  }

  readonly visible = signal(!this.ocultarPorRuta(this.router.url));

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.visible.set(!this.ocultarPorRuta(e.urlAfterRedirects)));
  }

  volver(): void {
    this.back.volver();
  }
}
